import React, { useCallback, useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'

import { useMutation } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/deprecated-design-system'
import { CalculateExpensesMileageDocument } from 'generated/resources/graphql'

import { fromCents } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'

import FromField from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Trip/FromField'
import StopField from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Trip/StopField'
import ToField from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Trip/ToField'
import { CustomizationProps } from 'modules/expenses/components/expenses/Detail/_newForm/withCustomizationFields'
import { PermissionsProps } from 'modules/expenses/components/expenses/Detail/_newForm/withFieldPermissions'

interface Props extends PermissionsProps, CustomizationProps {
  setIsCalculatingDistance?: (loading: boolean) => void
  hasMileageMultipleStops?: boolean
}

interface Place {
  lat: number
  lng: number
}

interface StopFormValue {
  name?: string | null
  latitude?: string | null
  longitude?: string | null
}

const EMPTY_STOPS: StopFormValue[] = []

interface CalculateMileageResponse {
  expensesMutations?: {
    calculateExpensesMileage?: {
      mileage?: {
        mileage?: number | null
        calculatedMileage?: number | null
      } | null
    } | null
  } | null
}

const Trip = (props: Props) => {
  const { setValue, watch } = useFormContext()
  const { setIsCalculatingDistance, hasMileageMultipleStops = false } = props
  const [calculateExpensesMileage] = useMutation<CalculateMileageResponse>(
    CalculateExpensesMileageDocument
  )

  // Track if we're initializing with existing data
  const isInitializedRef = useRef(false)
  const hasExistingData = Boolean(props.expensable?.id)

  // Watch form values
  const originLatitude = watch('originLatitude')
  const originLongitude = watch('originLongitude')
  const destinationLatitude = watch('destinationLatitude')
  const destinationLongitude = watch('destinationLongitude')
  const units = watch('units')
  const watchedStops: StopFormValue[] | undefined = watch('stops')
  const stops = watchedStops ?? EMPTY_STOPS

  const roundTrip = watch('roundTrip')

  const recalculateDistance = useCallback(async () => {
    if (!destinationLatitude || !destinationLongitude || !originLatitude || !originLongitude) {
      return
    }

    if (!setIsCalculatingDistance) {
      return
    }

    setIsCalculatingDistance(true)

    try {
      const currentStops: StopFormValue[] = watch('stops') ?? EMPTY_STOPS
      const validStops = currentStops.filter(
        (s: StopFormValue): s is StopFormValue & { latitude: string; longitude: string } =>
          Boolean(s.latitude && s.longitude)
      )

      const result = await calculateExpensesMileage({
        variables: {
          destinationLatitude: parseFloat(destinationLatitude),
          destinationLongitude: parseFloat(destinationLongitude),
          originLatitude: parseFloat(originLatitude),
          originLongitude: parseFloat(originLongitude),
          roundTrip: Boolean(roundTrip),
          units,
          stops: validStops.map((stop, position) => ({
            latitude: parseFloat(stop.latitude),
            longitude: parseFloat(stop.longitude),
            position,
          })),
        },
      })

      const mileageValue =
        result.data?.expensesMutations?.calculateExpensesMileage?.mileage?.mileage
      const calculatedMileageValue =
        result.data?.expensesMutations?.calculateExpensesMileage?.mileage?.calculatedMileage

      if (mileageValue != null && calculatedMileageValue != null) {
        setValue('mileage', fromCents(Number(mileageValue)), {
          shouldValidate: true,
          shouldDirty: true,
        })
        setValue('calculatedMileage', fromCents(Number(calculatedMileageValue)), {
          shouldValidate: true,
          shouldDirty: true,
        })
      }
    } finally {
      setIsCalculatingDistance(false)
    }
  }, [
    originLatitude,
    originLongitude,
    destinationLatitude,
    destinationLongitude,
    units,
    roundTrip,
    watch,
    setValue,
    setIsCalculatingDistance,
    calculateExpensesMileage,
  ])

  // Serialized stops coords for dependency tracking in useEffect
  // Only include stops with valid coordinates so adding/removing empty stops doesn't trigger recalculation
  const stopsCoordsKey = stops
    .filter((s: StopFormValue) => s.latitude && s.longitude)
    .map((s: StopFormValue) => `${s.latitude},${s.longitude}`)
    .join('|')

  // Watch for changes in coordinates and recalculate distance when both sets are available
  useEffect(() => {
    // Skip initial calculation when editing existing data
    if (hasExistingData && !isInitializedRef.current) {
      isInitializedRef.current = true
      return
    }

    if (originLatitude && destinationLatitude) {
      recalculateDistance()
    }
  }, [
    originLatitude,
    destinationLatitude,
    units,
    roundTrip,
    hasExistingData,
    stopsCoordsKey,
    recalculateDistance,
  ])

  const handleFromPlaceSelected = useCallback(
    (place: Place) => {
      setValue('originLatitude', String(place.lat), { shouldValidate: true, shouldDirty: true })
      setValue('originLongitude', String(place.lng), {
        shouldValidate: true,
        shouldDirty: true,
      })
    },
    [setValue]
  )

  const handleToPlaceSelected = useCallback(
    (place: Place) => {
      setValue('destinationLatitude', String(place.lat), {
        shouldValidate: true,
        shouldDirty: true,
      })
      setValue('destinationLongitude', String(place.lng), {
        shouldValidate: true,
        shouldDirty: true,
      })
    },
    [setValue]
  )

  const handleStopPlaceSelected = useCallback(
    (index: number, place: Place) => {
      setValue(`stops.${index}.latitude`, String(place.lat), {
        shouldValidate: true,
        shouldDirty: true,
      })
      setValue(`stops.${index}.longitude`, String(place.lng), {
        shouldValidate: true,
        shouldDirty: true,
      })
    },
    [setValue]
  )

  const handleAddStop = useCallback(() => {
    const current: StopFormValue[] = watch('stops') || []
    const next = [...current, { name: '', latitude: null, longitude: null }]
    setValue('stops', next, { shouldDirty: true })
  }, [watch, setValue])

  const handleRemoveStop = useCallback(
    (index: number) => {
      const current: StopFormValue[] = watch('stops') || []
      const next = current.filter((_, i) => i !== index)
      setValue('stops', next, { shouldDirty: true })
    },
    [watch, setValue]
  )

  return (
    <Box gap="s16">
      <FromField {...props} fieldIdentifier="from" onPlaceSelected={handleFromPlaceSelected} />

      {hasMileageMultipleStops &&
        stops.map((_stop: StopFormValue, index: number) => (
          <Box key={index} flexDirection="row" gap="s8" alignItems="center" width="full">
            <Box flexGrow width="full">
              <StopField
                {...props}
                fieldIdentifier="stops"
                index={index}
                onPlaceSelected={(place) => handleStopPlaceSelected(index, place)}
              />
            </Box>
            <Box paddingTop="s24">
              <Button type="secondary" onClick={() => handleRemoveStop(index)} iconName="delete" />
            </Box>
          </Box>
        ))}

      {hasMileageMultipleStops && (
        <Button type="secondary" onClick={handleAddStop}>
          {i18n.t('expenses_v2.forms.fields.stop.add')}
        </Button>
      )}

      <ToField {...props} fieldIdentifier="to" onPlaceSelected={handleToPlaceSelected} />
    </Box>
  )
}

export default Trip
